import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {ReCaptchaV3Service} from 'ngx-captcha';
import {Subscription} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {CareproviderHelper} from '../../../services/careprovider.helper';
import {EmailBookingService} from '../../../services/email-booking.service';
import {LoaderService} from '../../../services/loader.service';
import {ModalsControlService} from '../../../services/modals-control.service';
import {ToolsService} from '../../../services/tools.service';
import {EmailBookingPayload} from '../../../types/EmailBookingPayload';
import {InfoModalContent} from '../../../types/InfoModalContent';
import {ModalCommand} from '../../../types/ModalCommand';
import {BookingTypeEnum} from '../../../enums/BookingEnums';
import {EventActionEnum, EventCategoryEnum} from '../../../enums/GaTrackingEnums';
import {GtmEventEnum} from '../../../enums/GoogleEnums';
import {ModalTypeEnum} from '../../../enums/ModalEnums';
import {LoaderQueueItem} from '../../../types/LoaderQueueItem';
import {GtmService} from 'src/app/services/gtm.service';
import {DataLayerParamsClickGA4} from '../../../types/DataLayerParamsClickGA4';
import {GtmGa4EventPushService} from '../../../services/gtm-ga4-event-push.service';
import {BookingModalData} from '../../../types/BookingModalData';
import {AppConfigService} from '../../../services/app-config.service';
import {OpenHour} from '../../../types/OpenHour';
import {StaffMember} from '../../../types/StaffMember';
import {PhoneNumber} from '../../../types/PhoneNumber';
import {BookingSettings} from '../../../types/BookingSettings';
import {DLPageTypeEnum} from '../../../enums/DataLayerEnums';
import {phoneValidator} from '../../../app-common/validators/phone-number.validator';

@Component({
  selector: 'app-book',
  templateUrl: './book.component.html',
  styleUrls: ['./book.component.scss'],
})
export class BookComponent implements OnInit, OnDestroy {
  public bookForm: UntypedFormGroup;
  public bookingTypeEnum = BookingTypeEnum;
  public isBooking = true;
  public eventCategoryEnum = EventCategoryEnum;
  public eventActionEnum = EventActionEnum;
  public eventLabel = '';
  public siteKey: string;
  public doctorsAvailable: boolean;
  public customGa4Params: DataLayerParamsClickGA4 = null;
  private subscriptions: Subscription[] = [];
  private listaDigEnabled: boolean = false;
  private isProcessingBooking: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<BookComponent>,
    @Inject(MAT_DIALOG_DATA)
    public bookingModalData: BookingModalData,
    public careproviderHelper: CareproviderHelper,
    private formBuilder: UntypedFormBuilder,
    private loaderService: LoaderService,
    private bookingService: EmailBookingService,
    private tools: ToolsService,
    private gtmService: GtmService,
    private modalControl: ModalsControlService,
    private reCaptchaV3Service: ReCaptchaV3Service,
    // private bookingHelper: BookingHelperService,
    private gtmEventPush: GtmGa4EventPushService,
    private appConfigService: AppConfigService,
  ) {
    this.siteKey = appConfigService.appConfig.recaptchaSiteKey;
    // this.listaDigEnabled = this.bookingHelper.isListaDigEnabled(this.bookingModalData.bookingConfigurationData);
  }

  public ngOnInit(): void {
    if (this.listaDigEnabled) {
      this.eventLabel = `${this.bookingModalData.bookingConfigurationData.name}-${this.bookingModalData.bookingConfigurationData.id}`;
    }

    this.customGa4Params = this.buildDataLayerParamsClickGa4(
      [this.bookingModalData.bookingConfigurationData.bookingSettings],
      this.bookingModalData.bookingConfigurationData.mainCaregiverUnitTypeId,
      this.bookingModalData.bookingConfigurationData.areaId,
      this.bookingModalData.bookingConfigurationData.id,
    );

    this.doctorsAvailable = this.areDoctorsAvailable();
    this.buildBookForm();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public staffMemberTrackByFn(i: number, el: StaffMember) {
    return el ? el.id : null;
  }

  public phoneTrackByFn(i: number, el: PhoneNumber) {
    return el ? el.number : null;
  }

  public isTelephoneBooking() {
    return (
      this.isBooking &&
      this.bookingModalData.bookingConfigurationData.bookingSettings.bookingType.name === this.bookingTypeEnum.TEL
    );
  }

  public isEmailBooking() {
    return (
      this.isBooking &&
      this.bookingModalData.bookingConfigurationData.bookingSettings.bookingType.name === this.bookingTypeEnum.EMAIL
    );
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  public saveBooking(): void {
    if (this.isProcessingBooking) {
      // We should datadog this issue.
      return;
    }
    this.isProcessingBooking = true;
    this.reCaptchaV3Service.execute(
      this.siteKey,
      'book',
      (token) => {
        this.bookForm.controls.recaptcha.setValue(token || '');

        this.saveBookingTracking(EventActionEnum.EMAIL_BOOKING_TRY);

        (Object as any).values(this.bookForm.controls).forEach((control) => control.markAllAsTouched());
        if (this.bookForm.valid) {
          const queryId: number = this.loaderService.generateQueueId();
          const queryItem: LoaderQueueItem = {
            id: queryId,
            title: '',
            description: '',
          };
          this.loaderService.addToQueue(queryItem);
          this.subscriptions.push(
            this.bookingService
              .SendBooking(this.getBookingObjectFromFormData())
              .pipe(
                finalize(() => {
                  this.loaderService.removeFromQueue(queryId);
                  this.bookForm.reset();
                  this.isProcessingBooking = false;
                }),
              )
              .subscribe(
                () => this.processBookData(),
                () => this.openErrorInfoModal(),
              ),
          );
        }
      },
      {
        useGlobalDomain: false,
      },
      (error) => console.log(error),
    );
  }

  public openHoursTrackBy(i: number, el: OpenHour) {
    return el ? el.id : null;
  }

  private areDoctorsAvailable(): boolean {
    if (this.bookingModalData.bookingConfigurationData.careserviceId > 0) {
      return (
        this.bookingModalData.bookingConfigurationData.bookingSettings.staffMembers &&
        this.bookingModalData.bookingConfigurationData.bookingSettings.staffMembers.length > 0
      );
    } else {
      return this.bookingModalData.bookingConfigurationData.bookingSettings.mustPickStaff;
    }
  }

  private saveBookingTracking(action: EventActionEnum): void {
    let eventtext = `${this.bookingModalData.bookingConfigurationData.name}-${this.bookingModalData.bookingConfigurationData.id}`;
    if (!this.tools.isNil(this.bookingModalData.bookingConfigurationData.careserviceId)) {
      eventtext += `-${this.bookingModalData.bookingConfigurationData.careserviceName}-${this.bookingModalData.bookingConfigurationData.careserviceId}`;
    }

    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: EventCategoryEnum.EMAIL_BOOKING,
      Action: action,
      Label: eventtext,
      Value: 1,
    };
    this.gtmService.pushTag(gtmTag);

    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: `${EventCategoryEnum.EMAIL_BOOKING} - ${action}`,
      bookingType: this.bookingModalData.bookingConfigurationData.bookingSettings.bookingType.name,
      cpruId: this.bookingModalData.bookingConfigurationData.id,
      careServiceId: this.bookingModalData.bookingConfigurationData.careserviceId,
      pageType: this.bookingModalData.pageType,
    };
    const gtmGa4Tag = {
      event: GtmEventEnum.CTA_CLICK,
      ...customGa4Params,
    };
    this.gtmEventPush.pushClickEvent(gtmGa4Tag);
  }

  private processBookData(): void {
    this.saveBookingTracking(EventActionEnum.EMAIL_BOOKING_SEND);
    this.closeDialog();
    this.openInfoModal();
  }

  private openInfoModal(): void {
    const infoContent: InfoModalContent = {
      title: 'Tack för din bokningsförfrågan!',
      message: '<p>Du kommer att få ett bekräftelsemeddelande till din e-post.</p>',
    };
    const modalCommand: ModalCommand = {
      type: ModalTypeEnum.INFO,
      data: infoContent,
    };
    this.modalControl.openModal(modalCommand);
  }

  private openErrorInfoModal(): void {
    const infoContent: InfoModalContent = {
      title: 'Prova igen senare',
      message: '<p>Tyvärr, något gick fel.</p>',
    };
    const modalCommand: ModalCommand = {
      type: ModalTypeEnum.INFO,
      data: infoContent,
    };
    this.modalControl.openModal(modalCommand);
  }

  private buildBookForm(): void {
    const controls = {
      email: ['', [Validators.required, Validators.email]],
      message: ['', [Validators.required]],
      name: ['', [Validators.required]],
      tel: ['', [Validators.required, phoneValidator()]],
      staffid: ['', []],
      datapolicy: [false, [Validators.pattern('true')]],
      recaptcha: ['', [Validators.required]],
    };
    if (this.doctorsAvailable) {
      controls.staffid = ['', [Validators.required]];
    }
    this.bookForm = this.formBuilder.group(controls);
  }

  private getBookingObjectFromFormData(): EmailBookingPayload {
    return {
      CareProviderUnitId: this.bookingModalData.bookingConfigurationData.id,
      ServiceId: null,
      RequestedDate: null,
      Name: this.bookForm.value.name,
      Email: this.bookForm.value.email,
      Phone: this.bookForm.value.tel,
      Message: this.bookForm.value.message,
      StaffId: this.getStaffId(this.bookForm.value.staffid),
      AcceptTermsAndPrivacy: this.bookForm.value.datapolicy ? 'on' : '',
      recaptchaResponse: this.bookForm.value.recaptcha,
      recaptchaVersion: '3',
    };
  }

  private getStaffId(value: any): number {
    const staffId = Number.parseInt(value, 10);
    return isNaN(staffId) ? null : staffId;
  }

  private buildDataLayerParamsClickGa4(
    bookingSettings: BookingSettings[],
    mainCaregiverUnitTypeId: number,
    areaId: number,
    id: number,
  ): DataLayerParamsClickGA4 {
    return {
      callToAction: 'telefonnummer - visa telefonnummer',
      bookingType: bookingSettings && bookingSettings.length > 0 ? bookingSettings[0].bookingType?.name : null,
      inferredCareProviderTypeAreaId: areaId,
      mainCareProviderTypeId: mainCaregiverUnitTypeId,
      inferredCategoryTypeId: mainCaregiverUnitTypeId,
      cpruId: id,
      pageType: DLPageTypeEnum.CARE_SERVICE_PAGE,
    };
  }
}
